import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, GripVertical } from 'lucide-react';
import { db, GoalTemplate } from '../../lib/db';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface CreateMatterTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  goalTemplates: GoalTemplate[];
  onSuccess: () => void;
}

interface SelectedDeliverable {
  id: string;
  goalTemplateId: string;
  position: number;
}

function SortableDeliverable({ 
  deliverable, 
  goalTemplate,
  onRemove 
}: { 
  deliverable: SelectedDeliverable;
  goalTemplate: GoalTemplate | undefined;
  onRemove: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: deliverable.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  if (!goalTemplate) return null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg"
    >
      <div {...attributes} {...listeners} className="cursor-grab">
        <GripVertical className="h-4 w-4 text-gray-400" />
      </div>
      <div className="flex-1">
        <p className="font-medium text-gray-900">{goalTemplate.title}</p>
        {goalTemplate.description && (
          <p className="text-sm text-gray-600">{goalTemplate.description}</p>
        )}
      </div>
      <button
        onClick={onRemove}
        className="text-red-600 hover:text-red-700"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

export function CreateMatterTemplateModal({
  isOpen,
  onClose,
  userId,
  goalTemplates,
  onSuccess,
}: CreateMatterTemplateModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedDeliverables, setSelectedDeliverables] = useState<SelectedDeliverable[]>([]);
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (!isOpen) {
      setTitle('');
      setDescription('');
      setSelectedDeliverables([]);
    }
  }, [isOpen]);

  const handleAddDeliverable = (goalTemplateId: string) => {
    const newDeliverable: SelectedDeliverable = {
      id: `temp-${Date.now()}-${Math.random()}`,
      goalTemplateId,
      position: selectedDeliverables.length,
    };
    setSelectedDeliverables([...selectedDeliverables, newDeliverable]);
  };

  const handleRemoveDeliverable = (id: string) => {
    setSelectedDeliverables(selectedDeliverables.filter(d => d.id !== id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = selectedDeliverables.findIndex(d => d.id === active.id);
      const newIndex = selectedDeliverables.findIndex(d => d.id === over.id);
      
      const newDeliverables = [...selectedDeliverables];
      const [removed] = newDeliverables.splice(oldIndex, 1);
      newDeliverables.splice(newIndex, 0, removed);
      
      // Update positions
      newDeliverables.forEach((d, index) => {
        d.position = index;
      });
      
      setSelectedDeliverables(newDeliverables);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedDeliverables.length === 0) {
      alert('Please select at least one deliverable template');
      return;
    }

    setSaving(true);
    try {
      // Create the matter template
      const matterTemplate = await db.matterTemplates.create({
        user_id: userId,
        title,
        description: description || undefined,
        is_system: false,
        position: 0,
      });

      // Create the deliverable associations
      for (const deliverable of selectedDeliverables) {
        await db.matterTemplateDeliverables.create({
          matter_template_id: matterTemplate.id,
          goal_template_id: deliverable.goalTemplateId,
          position: deliverable.position,
        });
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error creating matter template:', error);
      alert('Failed to create matter template');
    } finally {
      setSaving(false);
    }
  };

  const availableTemplates = goalTemplates.filter(
    template => !selectedDeliverables.some(d => d.goalTemplateId === template.id)
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
      
      <div className="absolute inset-x-4 top-1/2 -translate-y-1/2 max-w-2xl mx-auto bg-white rounded-lg shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Create Matter Template</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Matter Template Title
              </label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description (Optional)
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  Deliverable Templates
                </label>
                {availableTemplates.length > 0 && (
                  <div className="relative">
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          handleAddDeliverable(e.target.value);
                          e.target.value = '';
                        }
                      }}
                      className="pl-3 pr-8 py-1 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      defaultValue=""
                    >
                      <option value="" disabled>Add deliverable...</option>
                      {availableTemplates.map(template => (
                        <option key={template.id} value={template.id}>
                          {template.title}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {selectedDeliverables.length === 0 ? (
                <p className="text-sm text-gray-500 italic">
                  No deliverables selected. Add at least one deliverable template.
                </p>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={selectedDeliverables.map(d => d.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {selectedDeliverables.map(deliverable => (
                        <SortableDeliverable
                          key={deliverable.id}
                          deliverable={deliverable}
                          goalTemplate={goalTemplates.find(t => t.id === deliverable.goalTemplateId)}
                          onRemove={() => handleRemoveDeliverable(deliverable.id)}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </div>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
              disabled={saving || selectedDeliverables.length === 0}
            >
              {saving ? 'Creating...' : 'Create Matter Template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}